import fetch from "lib/fetch";
import { getConfig } from "site-config";
import { formatMessage } from "lib/globalize-util";
import { ErrorTypes, FlowTypes } from "./enums";
import me from "models/me";

export const getLsidConfig = () => {
    const { LSID_API_ROOT, LSID_FEATURE_SSO, LSID_FEATURE_LSID, LSID_PRODUCT, api } = getConfig();
    return {
        root: LSID_API_ROOT,
        lsidLogin: `/auth/lightspeed/${LSID_PRODUCT}`,
        internalSSO: `${LSID_API_ROOT}/auth/sso/ls-onelogin?product=${LSID_PRODUCT}`,
        callback: api + "/auth/oidc/callback",
        hasSSO: LSID_FEATURE_SSO === "true",
        hasLsid: LSID_FEATURE_LSID === "true",
    };
};

export type Credentials = {
    email: string;
    password?: string;
    redirect_after_login?: string;
    rememberMe?: boolean;
};

export type ResponseType = {
    type?: FlowTypes;
    redirectUrl?: string;
    error?: {
        type: string;
        message?: string;
    };
};

export const buildCallbackUrl = ({
    redirect_after_login,
    rememberMe,
}: Pick<Credentials, "redirect_after_login" | "rememberMe">) => {
    const { callback } = getLsidConfig();
    const callbackUrl = callback.includes(window.location.protocol)
        ? new URL(callback)
        : new URL(callback, window.location.origin);

    if (redirect_after_login) {
        callbackUrl.searchParams.append("redirect_after_login", redirect_after_login);
    }

    if (rememberMe) {
        callbackUrl.searchParams.append("rememberMe", "true");
    }

    return callbackUrl;
};

const buildBody = ({ email, password }: Credentials) => {
    return {
        email: email.trim(),
        ...(password && { password: password.trim() }),
    };
};

export const lsOneLoginRedirect = (email: string, redirect_after_login: string): boolean => {
    const allFlags = me.ldClient.allFlags();

    if (!allFlags["lspd-sso"]) {
        return false;
    }

    const { hasSSO, hasLsid, internalSSO } = getLsidConfig();

    const isLsInternalEmail = email.endsWith("@lightspeedhq.com") && !email.includes("+");
    // If the user has a non aliased lightspeedhq.com domain return the LSONELOGIN type.
    // Because there is no mapping maintained in the db bypass the call to lsidLogin.
    if (hasLsid && hasSSO && isLsInternalEmail) {
        const callbackUrl = buildCallbackUrl({
            redirect_after_login: redirect_after_login,
            rememberMe: false,
        });
        const internalSSOUrl = new URL(internalSSO);
        internalSSOUrl.searchParams.append("redirect_uri", callbackUrl.href);
        location.replace(internalSSOUrl.href);
        return true;
    }
    return false;
};

/**
 * POST request against the LS identity-service to check which auth flow the user should have access to
 * @param Credentials
 */
export const checkAuthenticationFlow = async (credentials: Credentials): Promise<ResponseType> => {
    const { root, lsidLogin, hasSSO, hasLsid } = getLsidConfig();

    if (!root) {
        throw {
            type: FlowTypes.LEGACY,
            error: {
                type: ErrorTypes.LSID_MISSING_ROOT_URL,
            },
        };
    }

    if (!hasLsid && !hasSSO) {
        throw {
            type: FlowTypes.LEGACY,
            error: {
                type: ErrorTypes.LSID_FEATURE_DISABLED,
            },
        };
    }

    const { email } = credentials || {};

    if (!email) {
        throw {
            error: {
                type: ErrorTypes.LSID_MISSING_EMAIL,
                message: formatMessage("Invalid email and or password"),
            },
        };
    }
    let response;
    try {
        const url = new URL(lsidLogin, root);
        const body = buildBody(credentials);

        response = await fetch(url.href, {
            method: "post",
            redirect: "follow",
            body: JSON.stringify(body),
            mode: "cors",
            noDomain: true,
        });
    } catch (e) {
        // @note
        // This means an error like `cors` or a network error,
        throw {
            error: {
                type: ErrorTypes.LSID_GENERAL_ERROR,
                message: formatMessage("Oops! Something went wrong. Please refresh and try again."),
            },
        };
    }
    // @note
    // A 200 means the user will go through the LSID or SSO flows
    if (response.status === 200) {
        const body = await response.json().catch((e) => (response.ok ? {} : e));

        if (body?.redirectUrl) {
            if (hasSSO) {
                return {
                    type: FlowTypes.SSO,
                    redirectUrl: body.redirectUrl,
                };
            }

            throw {
                type: FlowTypes.LEGACY,
                error: {
                    message: formatMessage("Invalid email and or password"),
                    type: ErrorTypes.LSID_NOT_AUTHORIZED,
                },
            };
        }

        if (hasLsid) {
            return {
                type: FlowTypes.LSID,
            };
        }

        throw {
            type: FlowTypes.LEGACY,
            error: {
                message: formatMessage("Invalid email and or password"),
                type: ErrorTypes.LSID_NOT_AUTHORIZED,
            },
        };
    }

    // @note
    // A 401 means the user does not have access to SSO or LSID
    if (response.status === 401) {
        throw {
            type: FlowTypes.LEGACY,
            error: {
                message: formatMessage("Invalid email and or password"),
                type: ErrorTypes.LSID_NOT_AUTHORIZED,
            },
        };
    }

    // @note
    // Any other type of responses mean that the user cannot login
    throw {
        error: {
            type: ErrorTypes.LSID_INVALID_LOGIN,
            message: formatMessage("Invalid email and or password"),
        },
    };
};
